import React, { useEffect, useState } from "react";
import { dashboardOverview } from "@/lib/api";
import { TrendingUp, TrendingDown, Activity, Zap } from "lucide-react";

export default function CombinedDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const d = await dashboardOverview();
        if (mounted) setData(d);
      } catch { /* */ }
    };
    load();
    const iv = setInterval(load, 20000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  if (!data) return null;
  const intra = data.intra || {};
  const us = data.us || {};

  return (
    <div data-testid="combined-dashboard" className="rounded-2xl border border-zinc-900 bg-gradient-to-br from-[#0F0F0F] via-[#0A0A0A] to-black p-4 md:p-5 mb-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">// Combined Book · India + US Intraday</div>
          <div className="text-sm font-semibold text-white">Portfolio Overview</div>
        </div>
        <div className="flex items-center gap-2">
          <EngineBadge label="India" active={intra.bot_active} accent="#FFCC00" icon={<Activity className="w-3 h-3" />} />
          <EngineBadge label="US" active={us.bot_active} accent="#F472B6" icon={<Zap className="w-3 h-3" />} />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <EnginePanel title="Part 1 · India (NSE)" accent="#FFCC00" cur="₹" locale="en-IN" data={intra} exposureKey="exposure_inr" />
        <EnginePanel title="Part 2 · US (NASDAQ/NYSE)" accent="#F472B6" cur="$" locale="en-US" data={us} exposureKey="exposure_usd" />
      </div>
    </div>
  );
}

function EnginePanel({ title, accent, cur, locale, data, exposureKey }) {
  const fmt = (n) => `${cur}${Number(n || 0).toLocaleString(locale, { maximumFractionDigits: 2 })}`;
  const net = Number(data.net_pnl || 0);
  const today = Number(data.today_pnl || 0);
  return (
    <div className="rounded-xl border p-3" style={{ borderColor: `${accent}25`, background: `${accent}08` }}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: accent }}>{title}</div>
      <div className="grid grid-cols-3 gap-2">
        <Stat testid={`dash-${cur === "₹" ? "intra" : "us"}-net`} label="Net P&L" value={fmt(net)} tone={net > 0 ? "up" : net < 0 ? "down" : "flat"}
          icon={net >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />} />
        <Stat label="Today" value={fmt(today)} tone={today > 0 ? "up" : today < 0 ? "down" : "flat"} />
        <Stat label="Win Rate" value={`${(data.win_rate || 0).toFixed(1)}%`} />
        <Stat label="Active" value={`${data.active || 0}`} sub={`${data.settled || 0} closed`} />
        <Stat label="Exposure" value={fmt(data[exposureKey])} />
        <Stat label="Trades" value={`${data.trades || 0}`} />
      </div>
    </div>
  );
}

function Stat({ testid, label, value, tone, sub, icon }) {
  const cls = tone === "up" ? "text-emerald-400" : tone === "down" ? "text-red-400" : "text-white";
  return (
    <div data-testid={testid} className="rounded-lg border border-zinc-900 bg-zinc-950/60 px-2.5 py-2">
      <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500">{icon} {label}</div>
      <div className={`font-mono text-sm mt-0.5 truncate ${cls}`}>{value}</div>
      {sub && <div className="text-[10px] font-mono text-zinc-600 -mt-0.5">{sub}</div>}
    </div>
  );
}

function EngineBadge({ label, active, accent, icon }) {
  return (
    <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border flex items-center gap-1"
      style={{ borderColor: active ? `${accent}40` : "#27272A", color: active ? accent : "#71717A", background: active ? `${accent}10` : "transparent" }}>
      {icon} {label} {active ? "ON" : "OFF"}
    </span>
  );
}
